import React, { useEffect, useState } from "react";
import TableTree, { Rows } from "@atlaskit/table-tree";
import { getAction, getActionName, getFlow, renderTree } from "../../common/utils/integration.util";
import { useSelector, useDispatch } from "react-redux";
import lodash from "lodash";
import LogHeader from "./LogHeader";
import LogRow from "./LogRow";
import { useParams } from "react-router-dom";
import Modal, { ModalTransition, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@atlaskit/modal-dialog";
import ExecutionLogDetails from "./ExecutionLogDetails";
import LogFilters from "./LogFilters";
import Button from "@atlaskit/button/new";
import { getExecutionLog, getLogItem } from "../../redux/actions/logActions";
import { fetchFlowRunnerStatus } from "../../redux/actions/integrationActions";
import Spinner from "@atlaskit/spinner";
import Tooltip from "@atlaskit/tooltip";
import { useEscapeShortcut } from "../../common/utils/keybindings";
import { ActionType } from "../../common/interfaces/flow/action/ActionType";
import ScriptedLogDetails from "./ScriptedLogDetails";

const ExecutionLogPage = ({ isGlobal }) => {
  const dispatch = useDispatch();
  const { flowId } = useParams();

  const [tree, setTree] = useState([]);
  const [details, setDetails] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageTimestamp, setNextPageTimestamp] = useState(undefined);

  const logs = useSelector((state) => state.integration.logs);
  const flows = useSelector((state) => state.integration.flows);

  useEffect(() => {
    if (logs) {
      const newTree = renderTree(logs.result);

      // Update tree if data has changed
      if (!lodash.isEqual(tree, newTree) || logs.currentTime > currentTime) {
        setTree(newTree);
        setCurrentTime(logs.currentTime || Date.now());
      }

      // Manage pagination
      setNextPageTimestamp(logs.lastEvaluatedKey?.timestamp || undefined);
      setIsLoadingMore(false);
    }
  }, [logs, flowId]);

  useEffect(() => {
    if (!isGlobal && flowId) {
      dispatch(fetchFlowRunnerStatus(flowId));
    }
  }, [dispatch, flowId, isGlobal]);

    // Listen for updates to logs in the Redux store
  useEffect(() => {
    if (logs && details) {
      const updatedDetails = logs.result.find(log => log.id === details.id);
      //console.log("details is",details)
      if (updatedDetails) {
        //console.log("update details")
        setDetails(JSON.parse(JSON.stringify(updatedDetails)));
      }
    }
  }, [logs]);

  const onEscape = () => {
    if (isOpen) {
      setOpen(false);
      setDetails(null);
    }
  };

  useEscapeShortcut(onEscape);

  const loadMore = () => {
    setIsLoadingMore(true);
    dispatch(getExecutionLog(logs?.lastEvaluatedKey?.flowId, null, null, nextPageTimestamp));
  };

  const showLogDetails = (details) => {
    if (!details?.s3LogLoaded && details?.scope?.key) {
      dispatch(getLogItem(details.flowId, details.scope.key)); // No .then() here
    }
    setDetails(details);
    setOpen(true);
  };

  const detailsFlow = details ? getFlow(details.flowId, flows) : null;
  const detailsAction = details ? getAction(detailsFlow, details.actionId) : null;
  const actionName = details ? detailsAction?.name || getActionName(details.actionId, detailsFlow) : "";
  const modalTitle = details ? `Log for ${actionName || details.actionId || "action"}` : "Execution Log Details";

  return (
    <div className={isGlobal ? "ihub-main-view" : ""}>
      {isGlobal ? <h3>Execution Log</h3> : <h4>Execution Log</h4>}
      <p>
        Here, you'll find detailed logs from each time the flow runs, providing insights into its activities and any issues that might have occurred.
      </p>
      <LogFilters isGlobal={isGlobal} flowId={flowId} />

      <div className="execution-log">
        <TableTree>
          <LogHeader isGlobal={isGlobal} />
          <Rows
            items={tree}
            render={(logItem) => (
              <LogRow logItem={logItem} isGlobal={isGlobal} openDetailsLogview={showLogDetails} />
            )}
          />
        </TableTree>
      </div>

      {tree.length === 0 && (
        <span className="ihub-log-noshow">
          {isGlobal
            ? "No logs found, select an integration flow and click 'Filter' to load log entries."
            : "No logs found, click 'Filter' to reload."}
        </span>
      )}

      {tree.length > 0 && (
        <div className="load-more-button-container">
          <span className="load-more-row-count">Number of rows: {tree.length}</span>
          {nextPageTimestamp && (
            <Button className="load-more-button" onClick={loadMore} isDisabled={isLoadingMore}>
              {isLoadingMore ? (
                <>
                  <Spinner size="small" interactionName="load-more" label="Loading more logs" />
                  {" "}Loading...
                </>
              ) : (
                "Load more"
              )}
            </Button>
          )}
        </div>
      )}

      <ModalTransition>
        {isOpen && (
          <Modal shouldCloseOnOverlayClick={false} width={1000} height={1000}>
            <ModalHeader>
              <ModalTitle>{modalTitle}</ModalTitle>
            </ModalHeader>
            <ModalBody>
              {details?.actionType === ActionType.SCRIPT_ACTION_TYPE ? (
                <ScriptedLogDetails data={details} />
              ) : (
                <ExecutionLogDetails data={details} action={detailsAction} />
              )}
            </ModalBody>
            <ModalFooter>
              <Tooltip content="Press Escape to Close">
                <Button appearance="subtle" onClick={onEscape}>
                  Close
                </Button>
              </Tooltip>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </div>
  );
};

export default ExecutionLogPage;
